import suneditor from "suneditor";
import contentsCss from "suneditor/css/contents?inline";
import ko from "suneditor/langs/ko";
import {
  align,
  backgroundColor,
  blockStyle,
  fontColor,
  fontSize,
  list,
  table,
} from "suneditor/plugins";
import "suneditor/css/editor";
import documentCss from "./document-editor-content.css?inline";

type Editor = ReturnType<typeof suneditor.create>;
type Options = {
  initialContent?: string;
  cssTemplate?: string;
  height?: number;
  onReady?: (content: string) => void;
  onChange?: (content: string) => void;
  onFullscreenChange?: (fullscreen: boolean) => void;
};
type Session = {
  editor: Editor;
  ready: boolean;
  settle: () => void;
  change: () => void;
  style: HTMLStyleElement | null;
  cssTemplate: string;
  pendingContent?: string;
  cleanup: () => void;
};
const sessions = new Map<string, Session>();

/** 표 편집용 포장은 출력에 필요하지 않으며 브라우저 기본 figure 여백을 유발한다. */
function serialize(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const figure of doc.querySelectorAll<HTMLElement>("figure.se-input-component")) {
    const table = figure.querySelector<HTMLTableElement>(":scope > table");
    if (!table || figure.children.length !== 1) {
      continue;
    }
    if (figure.style.width) {
      table.style.width = figure.style.width;
    }
    if (figure.style.float) {
      table.style.float = figure.style.float;
    }
    figure.replaceWith(table);
  }
  // 소스 clean()과 WYSIWYG get()의 빈 셀 표현을 동일하게 맞춘다.
  for (const cell of doc.querySelectorAll("td,th")) {
    const child = cell.firstElementChild;
    if (
      cell.children.length === 1 &&
      child?.matches("div,p") &&
      !child.attributes.length &&
      /^\s*<br\s*\/?>\s*$/i.test(child.innerHTML)
    ) {
      cell.replaceChildren(doc.createElement("br"));
    }
  }
  if (!(doc.body.textContent?.trim() || doc.body.querySelector("table,img,hr"))) {
    return "";
  }
  return doc.body.innerHTML;
}

function read(editor: Editor): string {
  // html.get()은 소스 보기의 아직 적용되지 않은 입력을 읽지 않는다.
  const html: unknown = editor.$.frameContext.get("isCodeView")
    ? editor.$.html.clean(editor.$.frameContext.get("code").value)
    : editor.$.html.get();
  return serialize(typeof html === "string" ? html : "");
}

function setStyle(session: Session, cssTemplate = "") {
  session.cssTemplate = cssTemplate;
  if (!session.style) {
    return;
  }
  const hostStyle = getComputedStyle(document.documentElement);
  for (const token of ["--color-label-normal", "--color-line-dark", "--color-bg-alternative"]) {
    session.style.ownerDocument.documentElement.style.setProperty(
      token,
      hostStyle.getPropertyValue(token),
    );
  }
  // 스타일을 iframe 내부로 한정해 양식 CSS와 관리자 화면 CSS의 충돌을 막는다.
  session.style.textContent = `@layer suneditor { ${contentsCss} }\n${documentCss}\n${cssTemplate}\n.sun-editor-editable figure.se-input-component { margin: 0; width: 100%; }`;
}

export function initDocumentEditor(selector: string, options: Options = {}): Promise<void> {
  destroyDocumentEditor(selector);
  const target = document.querySelector(selector);
  if (!(target instanceof HTMLTextAreaElement)) {
    return Promise.reject(new Error("문서 편집 영역을 찾을 수 없어요."));
  }
  return new Promise((resolve, reject) => {
    let session: Session | undefined;
    const timeout = window.setTimeout(() => {
      if (!active()) {
        return;
      }
      reject(new Error("문서 편집기를 불러오지 못했어요. 창을 다시 열어 주세요."));
      destroyDocumentEditor(selector);
    }, 10_000);
    const settle = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    const active = () => session && sessions.get(selector) === session;
    const change = () => {
      if (active() && session?.ready) {
        options.onChange?.(read(session.editor));
      }
    };
    try {
      const editor = suneditor.create(target, {
        lang: ko,
        // 색상 플러그인이 없으면 붙여넣은 문서의 글자색·강조색도 제거된다.
        plugins: [align, backgroundColor, blockStyle, fontColor, fontSize, list, table],
        buttonList: [
          ["undo", "redo", "blockStyle", "fontSize"],
          ["bold", "italic", "underline", "align", "table", "list"],
          // 전체화면은 팝업이 맡는다(팝업 머리의 전체화면 버튼) — 편집기만 따로 전체화면이 되는 버튼은 두지 않는다
          ["codeView"],
        ],
        value: options.initialContent ?? "",
        height: `${options.height ?? 450}px`,
        iframe: true,
        iframe_attributes: { title: "문서 본문 편집", sandbox: "allow-same-origin" },
        // biome-ignore lint/style/useNamingConvention: SunEditor 공개 옵션 이름
        iframe_cssFileName: [],
        statusbar: false,
        // 조직별 양식 CSS의 클래스도 보존한다. 태그·속성·스타일 필터는 유지한다.
        allowedClassName: "^[a-zA-Z_][a-zA-Z0-9_-]*$",
        attributeWhitelist: { ol: "start|reversed", li: "value" },
        elementBlacklist: "script|style|iframe|object|embed|form|input|button|svg",
        tagStyles: {
          figure: "display|width|height|padding|padding-bottom|float|vertical-align",
          "@line":
            "text-align|margin|margin-[a-z]+|padding|padding-[a-z]+|line-height|font-size|font-family|letter-spacing|text-indent",
          "table|th|td":
            "width|height|float|border|border-[a-z]+|border-collapse|padding|padding-[a-z]+|margin|margin-[a-z]+|color|background-color|text-align|vertical-align|font-size|font-weight|font-style|text-decoration|table-layout",
        },
        events: {
          onload() {
            if (!(active() && session)) {
              return;
            }
            const doc = session.editor.$.frameContext.get("_wd");
            if (!doc) {
              reject(new Error("문서 편집 영역을 불러오지 못했어요."));
              destroyDocumentEditor(selector);
              return;
            }
            session.style = doc.createElement("style");
            doc.head.append(session.style);
            setStyle(session, session.cssTemplate);
            if (session.pendingContent !== undefined) {
              session.editor.$.html.set(session.pendingContent);
              session.pendingContent = undefined;
            }
            const handleEscape = (event: KeyboardEvent) => {
              if (event.key === "Escape" && session?.editor.$.frameContext.get("isFullScreen")) {
                event.preventDefault();
                event.stopImmediatePropagation();
                session.editor.$.viewer.fullScreen(false);
              }
            };
            window.addEventListener("keydown", handleEscape, true);
            doc.addEventListener("keydown", handleEscape, true);
            // 소스 입력에는 SunEditor의 onInput 이벤트가 전달되지 않는다.
            const code = session.editor.$.frameContext.get("code");
            code?.addEventListener("input", change);
            session.cleanup = () => {
              window.removeEventListener("keydown", handleEscape, true);
              doc.removeEventListener("keydown", handleEscape, true);
              code?.removeEventListener("input", change);
            };
            session.ready = true;
            session.editor.$.history.reset();
            options.onReady?.(read(session.editor));
            settle();
          },
          onInput: change,
          onKeyUp: change,
          onChange: change,
          onBlur: change,
          onToggleCodeView: change,
          onToggleFullScreen({ is }) {
            options.onFullscreenChange?.(is);
          },
        },
      });
      session = {
        editor,
        ready: false,
        settle,
        change,
        style: null,
        cssTemplate: options.cssTemplate ?? "",
        cleanup: () => {
          /* 준비 전에는 등록된 이벤트가 없다. */
        },
      };
      sessions.set(selector, session);
      setStyle(session, options.cssTemplate);
    } catch (error) {
      window.clearTimeout(timeout);
      destroyDocumentEditor(selector);
      reject(error);
    }
  });
}

export function destroyDocumentEditor(selector: string): void {
  const session = sessions.get(selector);
  if (!session) {
    return;
  }
  sessions.delete(selector);
  session.cleanup();
  // 전체화면에서 닫아도 페이지 스크롤 상태를 복구한다.
  if (session.ready) {
    session.editor.$.viewer.fullScreen(false);
  }
  session.editor.destroy();
  session.settle();
}

/** null은 미준비/종료, 빈 문자열은 사용자가 지운 본문이다. */
export function getContent(selector: string): string | null {
  const session = sessions.get(selector);
  return session?.ready ? read(session.editor) : null;
}

export function setContent(selector: string, content: string, cssTemplate = ""): void {
  const session = sessions.get(selector);
  if (!session) {
    return;
  }
  setStyle(session, cssTemplate);
  if (!session.ready) {
    session.pendingContent = content;
    return;
  }
  session.editor.$.html.set(content);
  session.change();
}

export function insertAtCursor(selector: string, content: string): void {
  const session = sessions.get(selector);
  if (!session?.ready) {
    return;
  }
  session.editor.$.viewer.codeView(false);
  session.editor.$.focusManager.focus();
  session.editor.$.html.insert(content);
  session.change();
}

/**
 * 편집 칸 높이 바꾸기(px) — 팝업 전체화면에서 편집기를 화면 높이만큼 늘리고, 돌아오면 원래 높이로.
 * SunEditor 는 height 옵션을 편집 칸(.se-wrapper-inner — 작성 · 코드 보기 두 칸)의 인라인 style 로 넣는다.
 * resetOptions 는 편집기를 다시 그려 입력 중인 상태가 흔들려서, 같은 자리의 인라인 높이만 바꾼다.
 */
export function setEditorHeight(selector: string, px: number): void {
  const target = document.querySelector(selector);
  const root = target?.parentElement;
  if (!root) {
    return;
  }
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(".se-wrapper-inner"))) {
    el.style.height = `${Math.round(px)}px`;
  }
}

const HIGHLIGHT_OK = "hereby-doc-var";
const HIGHLIGHT_BAD = "hereby-doc-var-bad";

/**
 * 본문의 {{…}} 를 편집 칸 안에서 칠한다 — 이 문서에서 채워지는 정보는 주황, 채워지지 않을 것(다른 문서용 · 오타)은 빨강.
 * 저장되는 HTML 은 건드리지 않는다: 글자를 span 으로 감싸지 않고 CSS Custom Highlight API(Range 표시)로만 칠한다.
 * 편집 칸은 iframe 문서라 DS 토큰(:root 변수)이 없다 — 색은 부모 문서에서 토큰 값을 읽어 넣는다.
 * 지원하지 않는 브라우저에서는 아무것도 하지 않는다(강조만 빠질 뿐 편집은 그대로).
 */
export function highlightPlaceholders(selector: string, isFilled: (name: string) => boolean): void {
  const frame = document.querySelector(selector)?.parentElement?.querySelector("iframe");
  const doc = frame?.contentDocument;
  const win = frame?.contentWindow as (Window & typeof globalThis) | null | undefined;
  if (!(doc?.body && win && "highlights" in win.CSS && typeof win.Highlight === "function")) {
    return;
  }
  if (!doc.getElementById(HIGHLIGHT_OK)) {
    const token = (name: string) =>
      getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const style = doc.createElement("style");
    style.id = HIGHLIGHT_OK;
    // 채워지는 것 = 주황 배경 + 본문 글자색, 채워지지 않는 것 = 빨강 배경 + 빨강 글자 + 물결 밑줄(색만으로 구분하지 않는다)
    style.textContent = `::highlight(${HIGHLIGHT_OK}){background-color:${token("--color-orange-100")};color:${token("--color-label-normal")}}
::highlight(${HIGHLIGHT_BAD}){background-color:${token("--color-red-100")};color:${token("--color-red-600")};text-decoration:underline wavy ${token("--color-red-600")}}`;
    doc.head.appendChild(style);
  }
  const ok: Range[] = [];
  const bad: Range[] = [];
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.textContent ?? "";
    for (const m of text.matchAll(/\{\{([^}]+)\}\}/g)) {
      const range = doc.createRange();
      range.setStart(node, m.index ?? 0);
      range.setEnd(node, (m.index ?? 0) + m[0].length);
      (isFilled(m[1]) ? ok : bad).push(range);
    }
  }
  win.CSS.highlights.set(HIGHLIGHT_OK, new win.Highlight(...ok));
  win.CSS.highlights.set(HIGHLIGHT_BAD, new win.Highlight(...bad));
}
